import React, { useEffect, useState, useContext, useRef, useMemo } from 'react';
import { AuthContext } from "../contexts/auth";
import CalendarWeekSelect from './calendar_week_select';
import { ChevronDownIcon, ChevronUpIcon, ChevronLeftIcon, ChevronRightIcon, DocumentPlusIcon, PaperClipIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import ComboboxMultiSelectAPI from './combobox_multiselect_autocomplete_api';
import { getCompanyAttendance } from '../services/api_calendar';
import { getEmployeesWithActiveContract } from '../services/api_calendar';
import { getMonthBlocked } from '../services/api_months_blocked';
import { Link } from 'wouter';
import Swal from 'sweetalert2';
import { getJustificationsCompanyList } from '../services/api_justifications';
import { deleteJustificationRequest } from '../services/api_justification_requests';
import EditGiustificativoModal from './modal_edit_giustificativo';
import AddGiustificativoModal from './modal_add_giustificativo';
import SelectGiustificativoModal from './modal_select_giustificativo';
import Fetching from './fetching';
import moment from 'moment';
import DayDetailsModal from './day_details_modal';
import CustomTooltipComponent from './custom_tooltip_component';



const EmployeeAttendanceWeekTable = ({ page, pageSize, setTotalResult, totalResult, setNextPage, setPrevPage, selectedUnit, onAbortControllerReady, employeeIds = [], date }) => {
    const { loading, token, profile } = useContext(AuthContext);
    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);
    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const [attendanceData, setAttendanceData] = useState(null);
    const [giustificativi, setGiustificativi] = useState([]);
    const [giustificativiMap, setGiustificativiMap] = useState({});
    const [showModalAddGiustificativo, setShowModalAddGiustificativo] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [selectedJustification, setSelectedJustification] = useState([]);
    const [selectedDay, setSelectedDay] = useState(null);
    const [justificationRequests, setJustificationRequests] = useState([]);
    const [justificationId, setJustificationId] = useState(null);
    const [selectedDescription, setSelectedDescription] = useState('');
    const [holidays, setHolidays] = useState([]);
    const tableContainerRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);
    const [scrollTop, setScrollTop] = useState(0);
    const firstHeaderRef = useRef(null);
    const [dipendenti, setDipendenti] = useState([]);
    const [modalDetailsData, setModalDetailsData] = useState();
    const [isModalDetailsOpen, setIsModalDetailsOpen] = useState(false);
    const controller = useRef(null);
    const [blockedMonths, setBlockedMonths] = useState([]);

    useEffect(() => {
        if (!date) return;

        const base = new Date(date);
        const week = getCompleteWeek(base);

        setCurrentWeek(week);

        const monthFirst = new Date(week[0].getFullYear(), week[0].getMonth(), 1);
        setSelectedMonth(monthFirst);
        setFirstDayOfMonth(monthFirst);
    }, [date]);

    const getCompleteWeek = (baseDate) => {
        const d = new Date(baseDate);
        const dow = d.getDay();
        const mondayDiff = dow === 0 ? -6 : 1 - dow;
        const monday = new Date(d);
        monday.setDate(d.getDate() + mondayDiff);
        const days = [monday];
        for (let i = 1; i < 7; i++) {
        const day = new Date(monday);
        day.setDate(monday.getDate() + i);
        days.push(day);
        }
        return days;
    };

    const formatDateISO = (d) => {
        const year = d.getFullYear();
        const month = `${d.getMonth() + 1}`.padStart(2, "0");
        const day = `${d.getDate()}`.padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    const initBaseDate = useMemo(() => (date ? new Date(date) : new Date()), [date]);

    const [currentWeek, setCurrentWeek] = useState(() => getCompleteWeek(initBaseDate));
    const [selectedMonth, setSelectedMonth] = useState(
        () => new Date(initBaseDate.getFullYear(), initBaseDate.getMonth(), 1)
    );
    const [firstDayOfMonth, setFirstDayOfMonth] = useState(
        () => new Date(initBaseDate.getFullYear(), initBaseDate.getMonth(), 1)
    );


    useEffect(() => {
        if (!date) return;
        const week = getCompleteWeek(date);
        setCurrentWeek(week);
        setSelectedMonth(new Date(week[0].getFullYear(), week[0].getMonth(), 1));
    }, [date]);


    async function fetchWeekData(signal) {
        if (!currentWeek.length) return;
        try {
        const startDate = formatDateISO(new Date(currentWeek[0]));
        const endDate = formatDateISO(new Date(currentWeek[currentWeek.length - 1]));

        setIsLoading(true);
        setIsError(false);

        // pagina solo se non ho un filtro di dipendenti
        const shouldPaginate = (employeeIds?.length ?? 0) === 0;

        const attendanceResponse = await getCompanyAttendance(
            token,
            { startDate, endDate },
            selectedUnit?.id,
            employeeIds,
            shouldPaginate ? page : null,
            shouldPaginate ? pageSize : null,
            signal
        );

        setNextPage(attendanceResponse.next);
        setPrevPage(attendanceResponse.previous);
        setTotalResult(attendanceResponse.count);
        setAttendanceData(attendanceResponse.results);

        // holidays + elenco dipendenti
        const holidaysData = [];
        const newDip = [];
        (attendanceResponse.results || []).forEach((employee) => {
            (employee.days || []).forEach((day) => {
            (day.holidays || []).forEach((h) => {
                holidaysData.push({ date: day.date, name: h.name });
            });
            });
            newDip.push({
            first_name: employee.first_name,
            last_name: employee.last_name,
            cf: employee.cf,
            id: employee.id,
            email_auth: employee.email_auth,
            });
        });
        setDipendenti(newDip);
        setHolidays(holidaysData);

        // permessi
        const requiredAttendancePermissions = ["hr.view_attendance_ou", "hr.view_attendance_all"];
        const hasAttendancePermission =
            profile.customer_type !== "employee" ||
            requiredAttendancePermissions.some((p) => profile.employee.permissions.includes(p));

        const requiredJustificationPermissions = ["hr.manage_justification_all", "hr.manage_justification_ou"];
        const hasJustificationPermission =
            profile.customer_type !== "employee" ||
            requiredJustificationPermissions.some((p) => profile.employee.permissions.includes(p));

        if (!hasAttendancePermission) {
            (attendanceResponse.results || []).forEach((employee) => {
            (employee.days || []).forEach((day) => {
                day.attendance = [];
            });
            });
            setAttendanceData(attendanceResponse.results);
        }

        if (!hasJustificationPermission) {
            (attendanceResponse.results || []).forEach((employee) => {
            (employee.days || []).forEach((day) => {
                day.justifications = [];
            });
            });
            setJustificationRequests([]);
        } else {
            const giustificativiData = await getJustificationsCompanyList(token);
            setGiustificativi(giustificativiData);
            const mapping = giustificativiData.reduce((acc, curr) => {
            acc[curr.justification.code] = curr.justification;
            return acc;
            }, {});
            setGiustificativiMap(mapping);
        }

        const blockedMonthsResponse = await getMonthBlocked(token, { startDate, endDate });
        const months = Array.isArray(blockedMonthsResponse)
            ? blockedMonthsResponse
            : Array.isArray(blockedMonthsResponse?.results)
            ? blockedMonthsResponse.results
            : [];
        setBlockedMonths(months);

        setIsLoading(false);
        } catch (error) {
        if (error?.name !== "CanceledError") {
            console.error(error);
            setIsError(true);
            setIsLoading(false);
        }
        }
    }

    /** Effettua il fetch quando cambiano token/week/unit/filtri/paginazione */
    useEffect(() => {
        controller.current?.abort();
        controller.current = new AbortController();
        const signal = controller.current.signal;
        onAbortControllerReady?.(controller.current);
        fetchWeekData(signal);
        return () => controller.current?.abort();
    }, [token, currentWeek, selectedUnit, employeeIds, page, pageSize]);


    const isDayBlocked = (d) =>
        blockedMonths.some((m) => Number(m.year) === d.getFullYear() && Number(m.month) === d.getMonth() + 1);


    const formatDateWeekSelected = (date) => {
        if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
            console.error('Invalid date provided:', date);
            return 'Invalid Date';
        }

        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const formatDay = (date) => {
        if (!date) return "";

        const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
        .toISOString().split('T')[0];

        const dayOfWeek = date.getDay();
        const formattedDate = date.toISOString().split('T')[0];

        const isHoliday = holidays.some(holiday => holiday.date === localDate);
    
        const options = { weekday: 'long', day: 'numeric' };
    
        if (isHoliday) {
            return (
                <div className="text-orange-500 font-bold">
                    {date.toLocaleDateString('it-IT', options)}
                </div>
            );
        } else if (dayOfWeek === 0) {

            return (
                <div className="text-emerald-500 font-bold">
                    {date.toLocaleDateString('it-IT', options)}
                </div>
            );
        } else {
            return (
                <div className="text-gray-600">
                    {date.toLocaleDateString('it-IT', options)}
                </div>
            );
        }
    };
    
    const refreshEmployeeWeek = async (employeeId) => {
        const startDate = formatDateISO(currentWeek[0]);
        const endDate = formatDateISO(currentWeek[currentWeek.length - 1]);

        const response = await getCompanyAttendance(
            token,
            { startDate, endDate },
            selectedUnit?.id,
            [employeeId],
            1,
            1
        );

        const updatedEmployee = response.results?.find(
            emp => String(emp.id) === String(employeeId)
        );

        if (!updatedEmployee) return;

        const updatedDaysMap = new Map(
            (updatedEmployee.days || []).map(day => [day.date, day])
        );

        setAttendanceData(prev =>
            (prev || []).map(emp =>
                String(emp.id) === String(employeeId)
                    ? {
                        ...emp,
                        days: emp.days.map(day =>
                            updatedDaysMap.get(day.date) || day
                        )
                    }
                    : emp
            )
        );
    };

    const getEmptyCellsAtStart = (firstDayOfWeek) => {
        if (firstDayOfWeek instanceof Date) {
            const startDayOfWeek = firstDayOfWeek.getDay();
            return startDayOfWeek === 0 ? 6 : startDayOfWeek - 1;
        } else {
            return 0;
        }
    };

    const getEmptyCellsAtEnd = (lastDayOfWeek) => {
        if (lastDayOfWeek instanceof Date) {
            const endDayOfWeek = lastDayOfWeek.getDay();
            return endDayOfWeek === 0 ? 0 : 7 - endDayOfWeek;
        } else {
            return 0;
        }
    };
    
    const emptyCellsAtStart = getEmptyCellsAtStart(currentWeek[0]);
    const emptyCellsAtEnd = getEmptyCellsAtEnd(currentWeek[currentWeek.length - 1]);

    const openModalAddGiustificativo = (dipendente, formattedDay, attendanceForDay) => {
        setShowModalAddGiustificativo(true);
        setSelectedEmployee(dipendente);
        setSelectedDay(formattedDay);
        setSelectedJustification(attendanceForDay);
    };

    const handleUpdateJustification = async ({ dayData, justificationId, startISO, endISO, note, employee }) => {
        try {
            const updatedJustification = {
                ...dayData,
                id: justificationId,
                datetime_from: startISO,
                datetime_to: endISO,
                note,
            };

            const formattedDay = moment(startISO).format('YYYY-MM-DD');

            const attendanceResponse = await getCompanyAttendance(
                token,
                { startDate: formattedDay, endDate: formattedDay },
                selectedUnit?.id,
                [employee.id],
                1,
                1
            );

            const updatedEmployee = attendanceResponse.results.find(emp => emp.id === employee.id);
            if (!updatedEmployee) return;

            const updatedDay = updatedEmployee.days.find(day => day.date === formattedDay);
            if (!updatedDay) return;

            setAttendanceData((prevData) =>
                prevData.map((emp) =>
                    emp.id === employee.id
                        ? { ...emp, days: emp.days.map((day) => (day.date === formattedDay ? updatedDay : day)) }
                        : emp
                )
            );

            setSelectedJustification(updatedJustification);
        } catch (error) {
            console.error("Errore durante l'aggiornamento del giustificativo:", error);
        }
    };

    const handleDeleteRequest = async (justificationId, employeeId, selectedJustification) => {
        const formattedDay = moment(selectedJustification.datetime_from).format('YYYY-MM-DD');

        const confirmDelete = await Swal.fire({
            icon: 'warning',
            title: 'Conferma eliminazione',
            text: 'Stai eliminando un giustificativo approvato. L\'azione non è reversibile. Confermi di voler eliminare?',
            showCancelButton: true,
            confirmButtonText: 'Conferma',
            cancelButtonText: 'Annulla',
            cancelButtonColor: '#d1d5db',
        });

        if (!confirmDelete.isConfirmed) return;

        const loader = Swal.fire({
            title: 'Eliminazione in corso...',
            html: 'Attendere',
            allowOutsideClick: false,
            allowEscapeKey: false,
            showConfirmButton: false,
            didOpen: () => Swal.showLoading()
        });

        try {
            await deleteJustificationRequest(token, justificationId);

            setJustificationRequests(prev =>
                prev.filter(j => j?.justification?.id !== justificationId)
            );

            const attendanceResponse = await getCompanyAttendance(
                token,
                { startDate: formattedDay, endDate: formattedDay },
                selectedUnit?.id,
                [employeeId],
                1,
                1
            );

            const updatedEmployee = attendanceResponse.results.find(emp => emp.id === employeeId);
            if (!updatedEmployee) return;

            const updatedDay = updatedEmployee.days.find(day => day.date === formattedDay);
            if (!updatedDay) return;

            setAttendanceData((prevData) =>
                prevData.map((emp) =>
                    emp.id === employeeId
                        ? { ...emp, days: emp.days.map((day) => (day.date === formattedDay ? updatedDay : day)) }
                        : emp
                )
            );

            Swal.fire({
                icon: 'success',
                title: 'Richiesta eliminata!',
                text: 'La richiesta è stata eliminata con successo!',
                showConfirmButton: false,
                timer: 1000,
            });
        } catch (error) {
            console.error("Errore durante l'eliminazione del giustificativo:", error);
            Swal.fire({
                icon: 'error',
                title: 'Errore',
                text: 'Si è verificato un errore durante l\'eliminazione del giustificativo. Per favore riprova.',
                confirmButtonText: 'OK',
            });
        } finally {
            loader.close();
        }
    };
    
    const handleNewJustificationRequest = async (
        newJustificationRequest,
        employeeId,
        formattedDay
    ) => {
        try {
            // Aggiorna tutti i giorni della settimana del dipendente
            await refreshEmployeeWeek(employeeId);

            // Chiudi la modale dopo l'aggiornamento
            setShowModalAddGiustificativo(false);

        } catch (error) {
            console.error(
                "Errore durante il refresh dei giustificativi:",
                error
            );
        }
    };

    const handleOUChange = async (employeeId, formattedDay) => {
        try {
            const startDate = formattedDay;
            const endDate = formattedDay;

            const attendanceResponse = await getCompanyAttendance(
                token,
                { startDate, endDate },
                selectedUnit?.id,
                [employeeId],
                1,
                1
            );

            const updatedEmployee = attendanceResponse.results.find(emp => emp.id === employeeId);
            if (!updatedEmployee) return;

            const updatedDay = updatedEmployee.days.find(day => day.date === formattedDay);
            if (!updatedDay) return;

            setAttendanceData((prevData) =>
                prevData.map((employee) => {
                    if (employee.id !== employeeId) return employee;

                    const updatedDays = employee.days.map((day) =>
                        day.date === formattedDay ? updatedDay : day
                    );

                    return { ...employee, days: updatedDays };
                })
            );
        } catch (error) {
            console.error("Errore durante l'aggiornamento OU nel genitore:", error);
        }
    };
    
    // const handleSwalMessage = (employeeId, hours, justifications, day) => {
    //     if (!employeeId) {
    //         return;
    //     }
    
    //     const formattedDay = `${day.getDate()}/${day.getMonth() + 1}/${day.getFullYear()}`;
    
    //     let justificationHtml = '';
    //     const approvedJustifications = justifications.filter(j => j.status === 'approved');
    //     const pendingJustifications = justifications.filter(j => j.status === null);       
    
    //     if (approvedJustifications.length > 0) {
    //         justificationHtml += `
    //             <p style="padding-top: 10px;"><strong>Ore Giustificativi</strong></p>
    //             <ul>
    //                 ${approvedJustifications.map((justification) => {
    //                     let hours = (justification.duration / 60).toFixed(2);
    //                     if (hours.endsWith('.00')) {
    //                         hours = parseFloat(hours).toFixed(0);
    //                     }
    //                     const description = justification.description || justification.code;
    //                     return `<li>${description}: ${hours}</li>`;
    //                 }).join('')}
    //             </ul>
    //         `;
    //     }
    
    //     if (pendingJustifications.length > 0) {
    //         justificationHtml += `
    //             <p style="padding-top: 10px;"><strong>Richiesta in attesa di esito.<br/> Vai alle </strong><a href="/pending_justifications" style="color: #7c3aed; text-decoration: underline;">richieste da gestire</a></p>
    //         `;
    //     }
    
    //     const formattedDate = `${day.getFullYear()}-${(day.getMonth() + 1).toString().padStart(2, '0')}-${day.getDate().toString().padStart(2, '0')}`;
    //     const holidaysOnDay = holidays.filter(h => h.date === formattedDate);
    
    //     let holidayNamesSet = new Set();
    //     holidaysOnDay.forEach(holiday => {
    //         holidayNamesSet.add(holiday.name);
    //     });
    //     const uniqueHolidayNames = Array.from(holidayNamesSet);
    
    //     let holidayHtml = '';
    //     if (uniqueHolidayNames.length > 0) {
    //         holidayHtml = `<p style="padding-bottom: 10px;"><strong>Festività:</strong> ${uniqueHolidayNames.join(', ')}</p>`;
    //     }
    
    //     Swal.fire({
    //         title: `Dettaglio del giorno ${formattedDay}`,
    //         html: `
    //             <div>
    //                 ${holidayHtml}
    //                 <p><strong>Ore Turno:</strong> ${hours}</p>
    //                 ${justificationHtml}
    //             </div>
    //         `,
    //     });
    // };

    const handleOpenDetailsModal = (dipendente, formattedDay) => {
        if (!dipendente) return;

        setSelectedEmployee(dipendente);
        setModalDetailsData(formattedDay);
        setIsModalDetailsOpen(true);
    };
    
    const refetchWeekRowDay = async (employeeId, dayISO) => {
        try {
            const res = await getCompanyAttendance(
            token,
            { startDate: dayISO, endDate: dayISO },
            selectedUnit?.id,
            [employeeId],
            1,
            1
            );

            const updatedEmp = res?.results?.[0];
            if (!updatedEmp) return;

            const updatedDay = updatedEmp.days?.find(d => d.date === dayISO);
            if (!updatedDay) return;

            setAttendanceData(prev =>
            (prev || []).map(emp =>
                emp.id === employeeId
                ? { ...emp, days: emp.days.map(d => (d.date === dayISO ? updatedDay : d)) }
                : emp
            )
            );
        } catch (e) {
            console.error('refetchWeekRowDay error', e);
        }
    };

    const justificationColors = {
        'FE': '#fef3c7',
        'PE': '#bfdbfe',
        'RO': '#bfdbfe',
        'PD': '#bfdbfe',
        'EO': '#c4b5fd',
        'MAL': '#fecaca',
        'ST': '#fae8ff',
    };

    const getBackgroundColor = (key) => justificationColors[key] || '#f3f4f6';

    const isWeekend = (date) => {
        const dayOfWeek = new Date(date).getDay();
        return dayOfWeek === 0; // 0 = Domenica
    };

    const startDragging = (e) => {
        setIsDragging(true);
        setStartX(e.pageX - tableContainerRef.current.offsetLeft);
        setScrollLeft(tableContainerRef.current.scrollLeft);
    };

    const stopDragging = () => {
        setIsDragging(false);
    };

    const onDrag = (e) => {
        if (!isDragging) return;
        e.preventDefault();
        const x = e.pageX - tableContainerRef.current.offsetLeft;
        const walk = (x - startX) * 2; // Velocità dello scroll
        tableContainerRef.current.scrollLeft = scrollLeft - walk;
    };

    const handleScroll = () => {
        const container = tableContainerRef.current;
        if (container) {
            const scrollTop = container.scrollTop;
            setScrollTop(scrollTop);
        }
    };

    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', onDrag);
            document.addEventListener('mouseup', stopDragging);
        } else {
            document.removeEventListener('mousemove', onDrag);
            document.removeEventListener('mouseup', stopDragging);
        }

        const container = tableContainerRef.current;
        if (container) {
            container.addEventListener('scroll', handleScroll);
        }

        return () => {
            document.removeEventListener('mousemove', onDrag);
            document.removeEventListener('mouseup', stopDragging);
            if (container) {
                container.removeEventListener('scroll', handleScroll);
            }
        };
    }, [isDragging]);

    if (isError) return <div>Error loading data</div>;


    return (
        <div className='pt-8 rounded-b-md border-x border-gray-200 bg-gray-100'>
            <div className="overflow-x-auto" style={{ overflowY: 'scroll', overflowX: 'auto', height: '100%' }}>
                <div className="flex items-center pb-3 pl-3 bg-gray-100">
                    <div className="w-3 h-3 bg-emerald-200 rounded-full mr-1"></div>
                    <span className='block font-medium text-gray-700 uppercase text-xs mr-3'>Ore Turno</span>
                    <div className="w-3 h-3 bg-amber-200 rounded-full mr-1"></div>
                    <span className='block font-medium text-gray-700 uppercase text-xs mr-3'>Ferie</span>
                    <div className="w-3 h-3 bg-sky-200 rounded-full mr-1"></div>
                    <span className='block font-medium text-gray-700 uppercase text-xs mr-3'>Permessi/ROL</span>
                    <div className="w-3 h-3 bg-violet-200 rounded-full mr-1"></div>
                    <span className='block font-medium text-gray-700 uppercase text-xs mr-3'>Ex Festività</span>
                    <div className="w-3 h-3 bg-orange-200 rounded-full mr-1"></div>
                    <span className='block font-medium text-gray-700 uppercase text-xs mr-3'>Malattia</span>
                    <div className="w-3 h-3 bg-fuchsia-200 rounded-full mr-1"></div>
                    <span className='block font-medium text-gray-700 uppercase text-xs mr-3'>Straordinari</span>
                    <div className="w-3 h-3 bg-gray-200 rounded-full mr-1"></div>
                    <span className='block font-medium text-gray-700 uppercase text-xs'>Altro</span>
                </div>
                {isLoading ? (
                    <Fetching />
                ) : (
            
                    <div
                        ref={tableContainerRef}
                        style={{
                            overflowX: 'auto',
                            overflowY: 'auto',
                            cursor: isDragging ? 'grabbing' : 'grab',
                            maxHeight: '70vh',
                            position: 'relative'
                        }}
                        onMouseDown={startDragging}
                    >
                        <table className="min-w-full border divide-y divide-gray-200">
                            <thead ref={firstHeaderRef} style={{ position: 'sticky', top: 0, zIndex: 2, background: 'white' }}>
                                <tr>
                                    <th
                                        style={{
                                            position: 'sticky',
                                            left: 0,
                                            background: 'white',
                                            zIndex: 3,
                                            border: '2px solid #e5e7eb',
                                            minWidth: '7rem',
                                        }}
                                        className="pl-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase"
                                    >
                                        Dipendenti
                                    </th>
                                    {Array.from({ length: emptyCellsAtStart }).map((_, index) => (
                                        <th key={`empty-start-${index}`} className="py-3 px-4 border-2 border-gray-200 w-24 bg-gray-50"></th>
                                    ))}
                                    {currentWeek.map((day, index) => (
                                        <th key={index} className="py-3 px-4 border-2 border-gray-200 text-center w-24 text-xs bg-white font-semibold uppercase">
                                            {formatDay(day)}
                                        </th>
                                    ))}
                                    {Array.from({ length: emptyCellsAtEnd }).map((_, index) => (
                                        <th key={`empty-end-${index}`} className="py-3 px-4 border-2 border-gray-200 w-24 bg-gray-50"></th>
                                    ))}
                                    <th
                                        className="text-xs font-semibold text-gray-600 uppercase bg-gray-50 border-2 border-gray-200"
                                        style={{
                                            position: 'sticky',
                                            right: 0,
                                            background: 'white',
                                            zIndex: 2,
                                            border: '2px solid #e5e7eb',
                                            width: '20%',
                                        }}
                                    >
                                        Totali
                                    </th>
                                </tr>
                            </thead>

                            <tbody className="bg-white divide-y divide-gray-200">
                                {(() => {
                                    const employeesToShow = selectedEmployees.length > 0 ? selectedEmployees : dipendenti;

                                    if (!isLoading && employeesToShow.length === 0) {
                                        return (
                                            <tr>
                                                <td colSpan={9} className="pl-4 py-3 border-2 border-gray-200 bg-white text-left italic text-sm font-medium text-gray-600">Non sono presenti anagrafiche</td>
                                            </tr>
                                        );
                                    }

                                    return employeesToShow
                                        .map((dipendente) => {
                                            const employeeAttendance = attendanceData?.find((employee) => employee.id === dipendente.id)?.days || [];
                                            let totalPresenceHours = 0;
                                            let totalPresenceActualHours = 0;
                                            const justificationTotals = {};

                                            const employeeData = attendanceData?.find((employee) => employee.id === dipendente.id);
                                            
                                            
                                            const allJustificationTypes = new Set();

                                            currentWeek.forEach((day) => {
                                                const formattedDay = formatDateWeekSelected(day);
                                                employeesToShow.forEach((dipendente) => {
                                                    const attendanceForDay = employeeAttendance;
                                                    const dayData = attendanceForDay.find((attendance) => attendance.date === formattedDay);
                                                    if (dayData) {
                                                        (dayData.justifications || []).forEach((justification) => {
                                                            allJustificationTypes.add(justification.code);
                                                        });
                                                    }
                                                });
                                            });

                                            currentWeek.forEach((day) => {
                                                const formattedDay = formatDateWeekSelected(day);
                                                const attendanceForDay = employeeAttendance.find((attendance) => attendance.date === formattedDay);
                                                
                                                // Calcolo delle ore di presenza totali
                                                if (attendanceForDay && attendanceForDay.attendance_expected) {
                                                    totalPresenceHours += attendanceForDay.attendance_expected / 60;
                                                }

                                                // Calcolo delle ore effettive di presenza totali
                                                if (attendanceForDay && attendanceForDay.attendance_actual) {
                                                    totalPresenceActualHours += attendanceForDay.attendance_actual / 60;
                                                }
                            
                                                // Calcolo dei totali per ogni tipo di giustificativo
                                                (attendanceForDay?.justifications || []).forEach((justification) => {
                                                    const hours = justification.duration / 60;
                                                    justificationTotals[justification.code] = (justificationTotals[justification.code] || 0) + hours;
                                                });
                                            });
                            
                                            // Formatta i risultati dei totali dei giustificativi
                                            const formattedJustificationTotals = Object.entries(justificationTotals).map(([code, hours]) => {
                                                const formattedHours = Number.isInteger(hours) ? hours.toFixed(0) : hours.toFixed(2);
                                                const description = giustificativiMap[code]?.description || code;
                                                return { code, hours: formattedHours, description };
                                            });

                                            const justificationOrder = Array.from(allJustificationTypes);

                                            return (
                                                <tr key={dipendente.id}>
                                                    <td
                                                        style={{
                                                            position: 'sticky',
                                                            left: 0,
                                                            background: 'white',
                                                            zIndex: 1,
                                                            border: '2px solid #e5e7eb',
                                                            minWidth: '7rem',
                                                        }}
                                                        className="px-2 py-3 border-2 border-gray-200 text-left text-xs bg-white font-semibold text-gray-600 uppercase w-28"
                                                        title={dipendente.first_name ? `${dipendente.last_name} ${dipendente.first_name}` : dipendente.cf}
                                                    >
                                                        <Link href={`/dipendenti/${dipendente.cf}`} className="hover:text-indigo-700">
                                                            {dipendente.first_name ? `${dipendente.last_name} ${dipendente.first_name}`.slice(0, 25) : dipendente.cf}
                                                            {dipendente.first_name && `${dipendente.last_name} ${dipendente.first_name}`.length > 25 ? '...' : ''}
                                                        </Link>
                                                    </td>
                                                    {Array.from({ length: emptyCellsAtStart }).map((_, index) => (
                                                        <td key={`empty-start-${index}`} className="py-3 px-4 border-2 border-gray-200 w-24 bg-gray-50"></td>
                                                    ))}
                                                    {currentWeek.map((day, i) => {
                                                        const formattedDay = formatDateWeekSelected(day);
                                                        const attendanceForDay = employeeAttendance.find((attendance) => attendance.date === formattedDay);

                                                        const weekHours = attendanceForDay ? attendanceForDay.week_hours : null;
                                                        const shiftType = attendanceForDay ? attendanceForDay.shift_type : null;
                                            
                                    
                                                        let canAddJustification = (
                                                            shiftType === 'a_chiamata'
                                                            || (shiftType === 'turno' && weekHours && weekHours > 0)
                                                            || (shiftType === 'fisso' && weekHours && weekHours > 0)
                                                            || (shiftType === 'senza_calendario')
                                                        );
                                                        
                                                        const hasActiveContract = attendanceForDay?.has_active_contract === true;
                                                        let hours = '-';
                                                        let justifications = [];


                                                        if (attendanceForDay) {
                                                            // Recupera sempre i giustificativi
                                                            justifications = attendanceForDay.justifications || [];

                                                            // Mantiene invariata la logica delle ore previste
                                                            if (
                                                                attendanceForDay.shift_type !== null &&
                                                                (attendanceForDay.week_hours || attendanceForDay.shift_type === 'a_chiamata')
                                                            ) {
                                                                const attendanceHours = attendanceForDay.attendance_expected / 60;

                                                                hours = Number.isInteger(attendanceHours)
                                                                    ? attendanceHours.toFixed(0)
                                                                    : attendanceHours.toFixed(2);
                                                            }
                                                        }
                                                        
                                                        const isHoliday = holidays.some(holiday => holiday.date === formattedDay);
                                                        const weekend = isWeekend(day);

                                                        const orderedJustifications = justificationOrder.map((type) => {
                                                            return justifications.filter(justification => justification.code === type);
                                                        });

                                                        const hasJustifications = justifications.length > 0;
                                                        const showNoWeekHoursPattern = (attendanceForDay?.has_active_contract && !weekHours && shiftType !== 'senza_calendario' && shiftType !== 'a_chiamata');

                                                        return (
                                                            <td
                                                                key={i}
                                                                className='border-2 border-gray-200 text-center w-24 text-sm font-semibold uppercase p-1.5'
                                                                onClick={() => {
                                                                    if (shiftType !== 'senza_calendario' && shiftType !== 'a_chiamata' && !weekHours) {
                                                                        Swal.fire({
                                                                            title: 'Orario settimanale mancante',
                                                                            html: `Il dipendente non ha orario lavorativo settimanale.<br /> Vai su <a href="/dipendenti/${dipendente.cf}/edit?tab=2" style="color: #4338ca; text-decoration: underline;">MODIFICA CONTRATTO</a> per salvare l'orario.`,
                                                                            icon: 'info',
                                                                            confirmButtonText: 'Ok',
                                                                        });
                                                                    } else {
                                                                        if (profile?.customer_type === 'employee') {
                                                                            const hasRequiredPermission =
                                                                                profile.employee?.permissions?.includes('hr.view_shifts_ou') ||
                                                                                profile.employee?.permissions?.includes('hr.view_shifts_all') ||
                                                                                profile.employee?.permissions?.includes('hr.manage_shifts_ou') ||
                                                                                profile.employee?.permissions?.includes('hr.manage_shifts_all');
                                                                
                                                                            if (!hasRequiredPermission) {
                                                                                Swal.fire({
                                                                                    title: 'Non hai i permessi per eseguire questa azione.',
                                                                                    icon: 'warning',
                                                                                    confirmButtonText: 'Ok',
                                                                                });
                                                                                return;
                                                                            }
                                                                        }
                                                                
                                                                        // attendanceForDay && handleOpenDetailsModal(dipendente, formattedDay);
                                                                    }
                                                                }}
                                                                style={{
                                                                    position: 'relative',
                                                                    paddingBottom: '3.2rem',
                                                                    ...((hasActiveContract && isHoliday && weekHours && shiftType !== 'senza_calendario') ? SvgHoliday() : {}),
                                                                    ...((hasActiveContract && isHoliday && weekend && weekHours && shiftType !== 'senza_calendario') ? SvgHolidayWeekend() : {}),
                                                                    ...((hasActiveContract && weekend && weekHours && shiftType !== 'senza_calendario') ? SvgPattern() : {}),
                                                                    ...((hasActiveContract && shiftType === 'senza_calendario') && PatternNoCalendar()),
                                                                    ...(showNoWeekHoursPattern && PatternNoWeekHours()),
                                                                }}
                                                            >
                                                                <div
                                                                    className={`mb-1 mx-auto align-top flex justify-center top-2 absolute left-0 right-0 ${!['0', '-'].includes(hours)  ? 'bg-emerald-100' : ''}`}
                                                                    style={{
                                                                        minWidth: '90%',
                                                                        position: 'absolute',
                                                                        top: '5px',
                                                                        width: '90%',
                                                                        display: hours !== '0' ? 'flex' : 'none',
                                                                        justifyContent: 'center',
                                                                        backgroundColor: !['0', '-'].includes(hours)  ? '#d1fae5' : 'transparent',
                                                                        ...(!hasActiveContract && PatternInactiveEmployee()),
                                                                    }}
                                                                >
                                                                    
                                                                    <span className="px-3">{hours !== '-' ? `${hours}` : <span className='text-transparent'>-</span>}</span>
                                                                    
                                                                </div>

                                                                {hasJustifications && (
                                                                    <div className='flex flex-col' style={{ marginTop: '1.5rem' }}>
                                                                        {orderedJustifications.map((justificationsOfType, index) => {
                                                                            if (justificationsOfType.length > 0) {
                                                                                const totalHours = justificationsOfType.reduce((sum, j) => sum + j.duration, 0) / 60;
                                                                                let formattedTotalHours = Number.isInteger(totalHours) ? totalHours.toFixed(0) : totalHours.toFixed(2);
                                                                                if (formattedTotalHours.endsWith('.00')) {
                                                                                    formattedTotalHours = totalHours.toFixed(0);
                                                                                }
                                                                                
                                                                                const backgroundColor = justificationsOfType.some(j => j.status === 'approved')
                                                                                    ? shiftType === 'senza_calendario' &&
                                                                                    !justificationColors[justificationsOfType[0].code]
                                                                                        ? '#d6d3d1'
                                                                                        : getBackgroundColor(justificationsOfType[0].code)
                                                                                    : 'white';

                                                                                const description = justificationsOfType[0]?.description || justificationsOfType[0]?.code;
                                                                                const hasOuPair = justificationsOfType.some(j => j.ou);
                                                                                
                                                                                const onClick = () => {
                                                                                    handleOpenDetailsModal(dipendente, formattedDay);
                                                                                };

                                                                                return (
                                                                                    <CustomTooltipComponent 
                                                                                        key={`${justificationsOfType[0]?.code}-${index}`} 
                                                                                        title={hasOuPair ? justificationsOfType[0]?.ou_name : ''} 
                                                                                        placement="top-start"
                                                                                        onClick={onClick}
                                                                                    >
                                                                                        <span
                                                                                            className={`my-0.5 cursor-pointer relative w-full text-gray-700 flex items-center justify-center 
                                                                                                ${formattedTotalHours === '0' ? 'bg-white border border-y border-gray-200 shadow-md' : ''}`}
                                                                                            style={{ backgroundColor: backgroundColor || 'white' }}
                                                                                           onClick={onClick}
                                                                                        >
                                                                                            <span className="text-center flex-1">{formattedTotalHours !== '0' ? formattedTotalHours : '-'}</span>

                                                                                            {hasOuPair && (
                                                                                                <div 
                                                                                                    className="absolute top-0 right-0"
                                                                                                    style={{
                                                                                                        width: 0,
                                                                                                        height: 0,
                                                                                                        borderLeft: '10px solid transparent',
                                                                                                        borderTop: '10px solid gray',
                                                                                                    }}
                                                                                                />
                                                                                            )}
                                                                                        </span>
                                                                                    </CustomTooltipComponent>
                                                                                );
                                                                            } else {
                                                                                // Spazio vuoto per giustificativi mancanti
                                                                                return <span key={`empty-${index}`} className="my-0.5 text-gray-400">&nbsp;</span>;
                                                                            }
                                                                        })}
                                                                    </div>
                                                                )}


                                                                {/*                                                             
                                                                {hasOuPair && (
                                                                    <CustomTooltip title={justificationsOfType[0]?.ou_name} placement="top-start">
                                                                        <BuildingOffice2Icon className="w-4 h-4 text-gray-600 absolute right-1" />
                                                                    </CustomTooltip>
                                                                )} */}


                                                                {/* Button to add justification */}
                                                                {canAddJustification && (
                                                                    <div key={i}>
                                                                        <div
                                                                            className="bottom-0 border-t bg-white py-1 cursor-pointer"
                                                                            style={{
                                                                                position: 'absolute',
                                                                                bottom: '0',
                                                                                right: '0',
                                                                                width: '100%',
                                                                                textAlign: 'center',
                                                                                display: 'flex',
                                                                                justifyContent: 'space-around',
                                                                                alignItems: 'center',
                                                                                gap: '0.5rem',
                                                                            }}
                                                                        >
                                                                            <CustomTooltipComponent title="Visualizza dettagli" placement="top" arrow >
                                                                                <div 
                                                                                    className='hover:bg-indigo-100 w-full flex justify-center'
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();

                                                                                        const isEmployee = profile.customer_type === 'employee';
                                                                                        const hasPermission =
                                                                                            !isEmployee ||
                                                                                            profile.employee?.permissions?.some(p =>
                                                                                            [
                                                                                                'hr.manage_justification_all',
                                                                                                'hr.manage_justification_ou',
                                                                                                'hr.view_inouts_ou',
                                                                                                'hr.view_inouts_all',
                                                                                                'hr.manage_inouts_ou',
                                                                                                'hr.manage_inouts_all',
                                                                                                'hr.view_shifts_ou',
                                                                                                'hr.view_shifts_all',
                                                                                                'hr.manage_shifts_ou',
                                                                                                'hr.manage_shifts_all'
                                                                                            ].includes(p)
                                                                                            );

                                                                                        if (!hasPermission) {
                                                                                            Swal.fire({
                                                                                            icon: 'warning',
                                                                                            title: 'Non hai i permessi per eseguire questa azione',
                                                                                            showConfirmButton: true,
                                                                                            confirmButtonText: 'OK',
                                                                                            });
                                                                                            return;
                                                                                        }

                                                                                        handleOpenDetailsModal(dipendente, formattedDay);
                                                                                        }}
                                                                                >
                                                                                    <MagnifyingGlassIcon
                                                                                        className="h-4 w-4 text-gray-400 hover:text-indigo-500"
                                                                                    
                                                                                    />
                                                                                </div>
                                                                            </CustomTooltipComponent>

                                                                            {attendanceForDay && !isDayBlocked(new Date(formattedDay)) && (
                                                                                <CustomTooltipComponent title="Aggiungi giustificativo" placement="top" arrow>
                                                                                    <div 
                                                                                        className='hover:bg-indigo-100 w-full flex justify-center'
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            const requiredPermissions = ['hr.manage_justification_all', 'hr.manage_justification_ou'];
                                                                                            const hasPermission =
                                                                                            profile.customer_type !== 'employee' ||
                                                                                            requiredPermissions.some((permission) =>
                                                                                                profile.employee.permissions.includes(permission)
                                                                                            );

                                                                                            if (!hasPermission) {
                                                                                            Swal.fire({
                                                                                                icon: 'warning',
                                                                                                title: 'Non hai i permessi per eseguire questa azione',
                                                                                                showConfirmButton: true,
                                                                                                confirmButtonText: 'OK',
                                                                                            });
                                                                                            } else {
                                                                                                openModalAddGiustificativo(dipendente, formattedDay, attendanceForDay);
                                                                                            }
                                                                                        }}
                                                                                    >
                                                                                        <DocumentPlusIcon
                                                                                            className="h-4 w-4 text-gray-400 hover:text-indigo-500 cursor-pointer"
                                                                                        />
                                                                                    </div>
                                                                                </CustomTooltipComponent>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </td>
                                                        );
                                                    })}

                                                    {Array.from({ length: emptyCellsAtEnd }).map((_, index) => (
                                                        <td key={`empty-end-${index}`} className="py-3 px-4 border-2 border-gray-200 w-24 bg-gray-50"></td>
                                                    ))}

                                                    {/* COLONNA TOTALI */}
                                                    <td
                                                        style={{
                                                            position: 'sticky',
                                                            right: 0,
                                                            background: 'white',
                                                            zIndex: 1,
                                                            maxWidth: '20%',
                                                            verticalAlign: 'top',
                                                        }}
                                                        className="px-1 text-start text-xs font-semibold bg-gray-50 text-gray-600"
                                                    >
                                                        <div className='px-1 flex flex-col justify-start py-2'>
                                                            {/* Riga con intestazione "ORE PRESENZA" */}
                                                            <div className="flex items-start border-b border-gray-200 pb-0.5">
                                                                <span className="font-bold">PRESENZA</span>
                                                            </div>

                                                            {/* Riga per ore previste */}
                                                            <div className="flex justify-between items-center border-b border-gray-200 py-0.5">
                                                                <span className='pl-4'>Prevista</span>
                                                                <span className='bg-emerald-100 text-gray-900'>
                                                                    {Number.isInteger(totalPresenceHours) ? totalPresenceHours.toFixed(0) : totalPresenceHours.toFixed(2)}
                                                                </span>
                                                            </div>

                                                            {/* Calcolo ore effettive */}
                                                            <div className="flex justify-between items-center border-b border-gray-200 py-0.5">
                                                                <span className='pl-4'>Effettiva</span>
                                                                <span className='bg-emerald-100 text-gray-900'>
                                                                    {Number.isInteger(totalPresenceActualHours) ? totalPresenceActualHours.toFixed(0) : totalPresenceActualHours.toFixed(2)}
                                                                </span>
                                                            </div>

                                                            {/* Visualizzazione dei totali per ogni tipo di giustificativo */}
                                                            {formattedJustificationTotals.map(({ code, hours, description }, index) => {
                                                                const backgroundColor = getBackgroundColor(code);
                                                                return (
                                                                    <div key={index} className="flex justify-between items-center border-b border-gray-200 py-0.5">
                                                                        <CustomTooltipComponent title={description} placement='left'>
                                                                            <span className='pr-2'>{description}</span>
                                                                        </CustomTooltipComponent>
                                                                        <span className='text-gray-900' style={{ backgroundColor: backgroundColor || 'white' }}>
                                                                            {hours !== '0' ? hours : '-'}
                                                                        </span>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        });
                                })()}
                            </tbody>
                        </table>
                    
                    </div>
                )}
            </div>

            {showModalAddGiustificativo && (
                <AddGiustificativoModal
                    closeModalAddGiustificativi={() => setShowModalAddGiustificativo(false)}
                    dipendente={selectedEmployee}
                    formattedDay={selectedDay}
                    selectedJustification={selectedJustification}
                    onSaveJustificationRequest={handleNewJustificationRequest}
                    selectedUnit={selectedUnit}
                />
            )}

            {isModalDetailsOpen && (
                <DayDetailsModal 
                    isOpen={isModalDetailsOpen} 
                    onClose={() => setIsModalDetailsOpen(false)} 
                    formattedDay={modalDetailsData} 
                    employee={selectedEmployee}
                    onNewJustificationAdded={handleNewJustificationRequest}
                    onJustificationChanged={handleNewJustificationRequest}
                    onOUChanged={handleOUChange}
                    isMonthOpen={!isDayBlocked(new Date(modalDetailsData))}
                    onShiftsChanged={() => refetchWeekRowDay(selectedEmployee.id, modalDetailsData)}
                    holidayToShow={[
                        ...new Map(
                            holidays
                            .filter(h => h.date === modalDetailsData)
                            .map(h => [h.name, h])
                        ).values()
                    ]}
                />
            )}
        </div>
    );
};

export default EmployeeAttendanceWeekTable;

// const SvgPattern = () => {
//     return {
//         backgroundColor: '#ffffff',
//         backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='24' viewBox='0 0 12 24' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2322c55e' fill-opacity='0.21'%3E%3Cpath d='M2 0h2v12H2V0zm1 20c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM9 8c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zm-1 4h2v12H8V12z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
//         backgroundRepeat: 'repeat',
//     };
// };


// const SvgPattern = () => {
//     return {
//         backgroundColor: '#ffffff',
//         backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='88' height='24' viewBox='0 0 88 24'%3E%3Cg fill-rule='evenodd'%3E%3Cg id='autumn' fill='%2322c55e' fill-opacity='0.13'%3E%3Cpath d='M10 0l30 15 2 1V2.18A10 10 0 0 0 41.76 0H39.7a8 8 0 0 1 .3 2.18v10.58L14.47 0H10zm31.76 24a10 10 0 0 0-5.29-6.76L4 1 2 0v13.82a10 10 0 0 0 5.53 8.94L10 24h4.47l-6.05-3.02A8 8 0 0 1 4 13.82V3.24l31.58 15.78A8 8 0 0 1 39.7 24h2.06zM78 24l2.47-1.24A10 10 0 0 0 86 13.82V0l-2 1-32.47 16.24A10 10 0 0 0 46.24 24h2.06a8 8 0 0 1 4.12-4.98L84 3.24v10.58a8 8 0 0 1-4.42 7.16L73.53 24H78zm0-24L48 15l-2 1V2.18A10 10 0 0 1 46.24 0h2.06a8 8 0 0 0-.3 2.18v10.58L73.53 0H78z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
//         backgroundRepeat: 'repeat',
//     };
// };

const SvgPattern = () => {
    return {
        backgroundColor: '#ffffff',
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='48' height='32' viewBox='0 0 48 32' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2322c55e' fill-opacity='0.13'%3E%3Cpath d='M27 32c0-3.314 2.686-6 6-6 5.523 0 10-4.477 10-10S38.523 6 33 6c-3.314 0-6-2.686-6-6h2c0 2.21 1.79 4 4 4 6.627 0 12 5.373 12 12s-5.373 12-12 12c-2.21 0-4 1.79-4 4h-2zm-6 0c0-3.314-2.686-6-6-6-5.523 0-10-4.477-10-10S9.477 6 15 6c3.314 0 6-2.686 6-6h-2c0 2.21-1.79 4-4 4C8.373 4 3 9.373 3 16s5.373 12 12 12c2.21 0 4 1.79 4 4h2z' /%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        backgroundRepeat: 'repeat',
    };
};

const SvgHoliday = () => {
    return {
        backgroundColor: '#ffffff',
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='48' height='32' viewBox='0 0 48 32' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23fed7aa' fill-opacity='0.4'%3E%3Cpath d='M27 32c0-3.314 2.686-6 6-6 5.523 0 10-4.477 10-10S38.523 6 33 6c-3.314 0-6-2.686-6-6h2c0 2.21 1.79 4 4 4 6.627 0 12 5.373 12 12s-5.373 12-12 12c-2.21 0-4 1.79-4 4h-2zm-6 0c0-3.314-2.686-6-6-6-5.523 0-10-4.477-10-10S9.477 6 15 6c3.314 0 6-2.686 6-6h-2c0 2.21-1.79 4-4 4C8.373 4 3 9.373 3 16s5.373 12 12 12c2.21 0 4 1.79 4 4h2z' /%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        backgroundRepeat: 'repeat',
    };
};


const SvgHolidayWeekend = () => {
    return {
        backgroundColor: '#feead2',
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='48' height='32' viewBox='0 0 48 32' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M27 32c0-3.314 2.686-6 6-6 5.523 0 10-4.477 10-10S38.523 6 33 6c-3.314 0-6-2.686-6-6h2c0 2.21 1.79 4 4 4 6.627 0 12 5.373 12 12s-5.373 12-12 12c-2.21 0-4 1.79-4 4h-2zm-6 0c0-3.314-2.686-6-6-6-5.523 0-10-4.477-10-10S9.477 6 15 6c3.314 0 6-2.686 6-6h-2c0 2.21-1.79 4-4 4C8.373 4 3 9.373 3 16s5.373 12 12 12c2.21 0 4 1.79 4 4h2z' /%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        backgroundRepeat: 'repeat',
    };
};

const PatternInactiveEmployee = () => {
    return {
        backgroundColor: '#ffffff',
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='6' height='6' viewBox='0 0 6 6' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%234f46e5' fill-opacity='1' fill-rule='evenodd'%3E%3Cpath d='M5 0h1L0 6V5zM6 5v1H5z'/%3E%3C/g%3E%3C/svg%3E")`,
        backgroundRepeat: 'repeat',
    };
};

const PatternNoWeekHours = () => {
    return {
        backgroundColor: '#fffbeb',
        cursor: 'pointer',
    };
};

const PatternNoCalendar = () => {
    return {
        backgroundColor: '#f1f5f9',
        cursor: 'pointer',
    };
};